import { api } from "@/lib/api";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Field,
  Input,
  Label,
  Text,
} from "@a7y/tailwind-catalyst";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState, type ChangeEvent } from "react";

export function EditOrgNameDialog({
  open,
  orgId,
  currentName,
  onClose,
  onSuccess,
}: {
  open: boolean;
  orgId: string;
  currentName: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [name, setName] = useState(currentName);

  useEffect(() => {
    // OrganizationDetailPage.tsx:160 renders this dialog unconditionally, so it
    // never unmounts and useState(currentName) only ever runs once. Resetting
    // on open is the only way a reopen after a cancelled edit shows the real
    // name. Remounting via key={String(open)} would blank the fields during the
    // Headless UI exit transition.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (open) setName(currentName);
  }, [open, currentName]);

  const mutation = useMutation({
    mutationFn: () => api.updateOrganization(orgId, { name: name.trim() }),
    onSuccess,
  });

  const trimmed = name.trim();
  const hasChanges = trimmed.length > 0 && trimmed !== currentName;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!hasChanges || mutation.isPending) return;
    mutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} size="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle>Edit Organization Name</DialogTitle>
        <DialogBody>
          <Field>
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              autoFocus
              disabled={mutation.isPending}
            />
          </Field>
          {mutation.isError && (
            <Text className="mt-2 text-xs text-red-600">
              Failed: {(mutation.error as Error)?.message}
            </Text>
          )}
        </DialogBody>
        <DialogActions>
          <Button outline type="button" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="blue"
            disabled={!hasChanges || mutation.isPending}
          >
            {mutation.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
